/**
 * Section Hub
 *
 * Landing view for Content Control. The page manages five separate things and
 * used to present all of them at once inside one panel; this shows what each
 * section holds, and you click into the one you came for.
 *
 * @module HomeLayoutManager/components
 */

import React from 'react';
import { Box, Paper, Typography, Chip } from '@mui/material';
import {
  Reorder as HomeIcon,
  ViewCarousel as CarouselIcon,
  GridView as ShopIcon,
  LocalOffer as BrandsIcon,
  Image as BannersIcon,
  Category as AllIcon,
} from '@mui/icons-material';

import type { ConfigTab } from './ConfigurationPanel';
import type { DraftState } from '../types';

/**
 * Only what the hub needs off a brand. Structural on purpose — `types.ts` and
 * `api.ts` each declare their own `Brand` with different optionality, and this
 * card doesn't care which one it's handed.
 */
export interface HubBrand {
  id: string;
  name: string;
  position?: number;
}

// ============================================================================
// Section definitions
// ============================================================================

/**
 * Everything a card needs to describe its section.
 *
 * `brands` is the LIVE brand list, not the draft's. The Brands card used to count
 * `draft.brandPositions.positions`, which is only written when someone drags the
 * rail — a frozen snapshot of whatever existed at that moment, including brands
 * deleted since. That is why the card advertised 65 brands while the section
 * itself listed 27: the card and the section were reading different things. A
 * card must count what its section renders.
 */
export interface SectionContext {
  draft: DraftState | null;
  brands: HubBrand[] | null;
}

interface SectionDef {
  key: ConfigTab;
  label: string;
  what: string;
  icon: React.ReactElement;
  /** How many items this section currently shows. */
  count: (ctx: SectionContext) => number;
  /** A few item names, so the card says what's actually in there. */
  sample: (ctx: SectionContext) => string[];
}

const categories = (draft: DraftState | null) => draft?.categoryConfig?.categories ?? [];

/**
 * Live brands in rail order — draft positions where set, live position otherwise.
 * Falls back to the draft's own list only while the live list is still loading.
 */
function orderedBrands({ draft, brands }: SectionContext): string[] {
  const positions = draft?.brandPositions?.positions ?? [];
  if (!brands) {
    return [...positions].sort((a, b) => a.position - b.position).map((b) => b.name);
  }
  const posMap = new Map(positions.map((p) => [p.id, p.position]));
  return [...brands]
    .sort((a, b) => (posMap.get(a.id) ?? a.position ?? 999) - (posMap.get(b.id) ?? b.position ?? 999))
    .map((b) => b.name);
}

export const SECTIONS: SectionDef[] = [
  {
    key: 'home',
    label: 'Home',
    what: 'Product scrollers on the app home screen',
    icon: <HomeIcon />,
    count: ({ draft: d }) => categories(d).filter((c) => c.isActive && c.showOnHomePage).length,
    sample: ({ draft: d }) =>
      categories(d)
        .filter((c) => c.isActive && c.showOnHomePage)
        .sort((a, b) => a.scrollerPosition - b.scrollerPosition)
        .map((c) => c.name),
  },
  {
    key: 'carousel',
    label: 'Carousel',
    what: 'Round category icons above the scrollers',
    icon: <CarouselIcon />,
    count: ({ draft: d }) => categories(d).filter((c) => c.isActive && c.showInCarousel).length,
    sample: ({ draft: d }) =>
      categories(d)
        .filter((c) => c.isActive && c.showInCarousel)
        .sort((a, b) => a.carouselPosition - b.carouselPosition)
        .map((c) => c.name),
  },
  {
    key: 'shop',
    label: 'Shop',
    what: 'The category grid on the Shop tab',
    icon: <ShopIcon />,
    count: ({ draft: d }) => categories(d).filter((c) => c.isActive && c.showInShop).length,
    sample: ({ draft: d }) =>
      categories(d)
        .filter((c) => c.isActive && c.showInShop)
        .sort((a, b) => a.shopPosition - b.shopPosition)
        .map((c) => c.name),
  },
  {
    key: 'banners',
    label: 'Banners',
    what: 'Top, slider and bottom banner slots',
    icon: <BannersIcon />,
    count: ({ draft: d }) => (d?.bannerConfig?.positions ?? []).reduce((n, p) => n + (p.items?.length ?? 0), 0),
    sample: ({ draft: d }) => (d?.bannerConfig?.positions ?? []).flatMap((p) => (p.items ?? []).map((i) => i.name)),
  },
  {
    key: 'brands',
    label: 'Brands',
    what: 'The ordered brand rail',
    icon: <BrandsIcon />,
    // Live brands — see SectionContext. NOT draft.brandPositions, which is stale.
    count: (ctx) => ctx.brands?.length ?? ctx.draft?.brandPositions?.positions?.length ?? 0,
    sample: orderedBrands,
  },
  {
    key: 'all',
    label: 'All categories',
    what: 'Every category, including ones not shown anywhere',
    icon: <AllIcon />,
    count: ({ draft: d }) => categories(d).length,
    sample: ({ draft: d }) => categories(d).map((c) => c.name),
  },
];

// ============================================================================
// Component
// ============================================================================

export interface SectionHubProps {
  draft: DraftState | null;
  onOpen: (section: ConfigTab) => void;
  /** Sections touched since the last publish — surfaced on the card. */
  changedSections?: Partial<Record<ConfigTab, number>>;
  /** Live brand list, so the Brands card counts what the Brands section renders. */
  brands?: HubBrand[] | null;
}

export default function SectionHub({ draft, onOpen, changedSections, brands = null }: SectionHubProps): React.ReactElement {
  const ctx: SectionContext = { draft, brands };
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 650, letterSpacing: '-0.01em' }}>
          Sections
        </Typography>
        <Typography variant="caption" color="text.secondary">
          one draft, shared by every admin
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' },
        }}
      >
        {SECTIONS.map((section) => {
          const count = section.count(ctx);
          const sample = section.sample(ctx).slice(0, 4);
          const remaining = Math.max(0, count - sample.length);
          const changed = changedSections?.[section.key] ?? 0;

          return (
            <Paper
              key={section.key}
              role="button"
              tabIndex={0}
              onClick={() => onOpen(section.key)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpen(section.key);
                }
              }}
              sx={{
                p: 2,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
                border: 1,
                borderColor: 'divider',
                transition: 'border-color 0.15s, transform 0.15s, box-shadow 0.15s',
                '&:hover': {
                  borderColor: 'primary.main',
                  transform: 'translateY(-2px)',
                  boxShadow: 3,
                },
                '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
                '@media (prefers-reduced-motion: reduce)': {
                  transition: 'none',
                  '&:hover': { transform: 'none' },
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ color: 'text.secondary', display: 'flex' }}>{section.icon}</Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 650, flex: 1, minWidth: 0 }}>
                  {section.label}
                </Typography>
                {changed > 0 && (
                  <Chip
                    label={`${changed} changed`}
                    size="small"
                    color="warning"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                )}
                <Typography variant="h6" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                  {count}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">
                {section.what}
              </Typography>

              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {sample.map((name) => (
                  <Chip
                    key={name}
                    label={name}
                    size="small"
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.68rem', maxWidth: 150 }}
                  />
                ))}
                {remaining > 0 && (
                  <Chip
                    label={`+${remaining}`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 22, fontSize: '0.68rem', borderStyle: 'dashed', color: 'text.disabled' }}
                  />
                )}
                {count === 0 && (
                  <Typography variant="caption" color="text.disabled">
                    Nothing here yet
                  </Typography>
                )}
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}
